import React, { useState, useEffect } from 'react';
import { supabaseAdmin as supabase } from '../../lib/supabase.js';
import '../Timetable/styles/ModernButtons.css';

const tenantId = 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3';

const AppSetupManager = ({ onSetupComplete }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [setupStatus, setSetupStatus] = useState({
    appSetup: false,
    timetableSetup: false,
    tablesCreated: false
  });

  const showMessage = (text, type = 'info') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  useEffect(() => {
    checkSetupStatus();
  }, []);

  const checkSetupStatus = async () => {
    try {
      setLoading(true);
      
      // Check if tables exist by trying to query them
      const { data: timetableData, error: timetableError } = await supabase
        .from('saas_timetable_setup')
        .select('*')
        .eq('tenant_id', tenantId)
        .limit(1);

      const { data: appData, error: appError } = await supabase
        .from('saas_app_setup')
        .select('*')
        .eq('tenant_id', tenantId)
        .limit(1);

      setSetupStatus({
        appSetup: !appError && appData && appData.length > 0,
        timetableSetup: !timetableError && timetableData && timetableData.length > 0,
        tablesCreated: !timetableError && !appError
      });

    } catch (error) {
      console.error('Error checking setup status:', error);
      setSetupStatus({
        appSetup: false,
        timetableSetup: false,
        tablesCreated: false
      });
    } finally {
      setLoading(false);
    }
  };

  const createTables = async () => {
    try {
      setLoading(true);
      showMessage('Creating setup tables...', 'info');

      // Create timetable setup table
      const timetableTableSQL = `
        CREATE TABLE IF NOT EXISTS saas_timetable_setup (
          id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
          tenant_id UUID NOT NULL,
          mode VARCHAR(20) NOT NULL DEFAULT 'weekday',
          day_periods INTEGER NOT NULL DEFAULT 14,
          period_patterns JSONB NOT NULL DEFAULT '[]',
          week_days JSONB NOT NULL DEFAULT '["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]',
          created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
          updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
          UNIQUE(tenant_id)
        );
      `;

      const { error: timetableError } = await supabase.rpc('exec_sql', { 
        sql_query: timetableTableSQL 
      });

      if (timetableError) {
        console.log('Timetable table creation:', timetableError.message);
      }

      // Create app setup table
      const appTableSQL = `
        CREATE TABLE IF NOT EXISTS saas_app_setup (
          id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
          tenant_id UUID NOT NULL,
          app_name VARCHAR(100) NOT NULL DEFAULT 'MyClass',
          app_version VARCHAR(20) NOT NULL DEFAULT '1.0.0',
          academic_year VARCHAR(20) NOT NULL DEFAULT '2025-2026',
          school_name VARCHAR(200),
          school_address TEXT,
          school_phone VARCHAR(50),
          school_email VARCHAR(100),
          principal_name VARCHAR(100),
          created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
          updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
          UNIQUE(tenant_id)
        );
      `;

      const { error: appError } = await supabase.rpc('exec_sql', { 
        sql_query: appTableSQL 
      });

      if (appError) {
        console.log('App table creation:', appError.message);
      }

      showMessage('Tables created successfully!', 'success');
      await checkSetupStatus();

    } catch (error) {
      console.error('Error creating tables:', error);
      showMessage(`Error creating tables: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const insertDefaultData = async () => {
    try {
      setLoading(true);
      showMessage('Inserting default data...', 'info');

      // Insert default timetable setup
      const defaultTimetableSetup = {
        tenant_id: tenantId,
        mode: 'weekday',
        day_periods: 14,
        period_patterns: [
          { period_sequence: 1, period_node: '班主任節', time_slot: '7:45-8:10' },
          { period_sequence: 2, period_node: 'Period 1', time_slot: '8:10-8:45' },
          { period_sequence: 3, period_node: 'Period 2', time_slot: '8:45-9:20' },
          { period_sequence: 4, period_node: 'Period 3', time_slot: '9:20-9:55' },
          { period_sequence: 5, period_node: '小息', time_slot: '9:55-10:15' },
          { period_sequence: 6, period_node: 'Period 4', time_slot: '10:15-10:50' },
          { period_sequence: 7, period_node: 'Period 5', time_slot: '10:50-11:25' },
          { period_sequence: 8, period_node: '小息', time_slot: '11:25-11:30' },
          { period_sequence: 9, period_node: 'Period 6', time_slot: '11:30-12:05' },
          { period_sequence: 10, period_node: 'Period 7', time_slot: '12:05-12:40' },
          { period_sequence: 11, period_node: '午膳', time_slot: '12:40-1:50' },
          { period_sequence: 12, period_node: 'Period 8', time_slot: '1:50-2:25' },
          { period_sequence: 13, period_node: 'Period 9', time_slot: '2:25-3:00' },
          { period_sequence: 14, period_node: '班主任節', time_slot: '3:00-3:35' }
        ],
        week_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
      };

      const { error: timetableError } = await supabase
        .from('saas_timetable_setup')
        .upsert(defaultTimetableSetup);

      if (timetableError) {
        console.error('Error inserting timetable setup:', timetableError);
        showMessage(`Error inserting timetable setup: ${timetableError.message}`, 'error');
        return;
      }

      // Insert default app setup
      const defaultAppSetup = {
        tenant_id: tenantId,
        app_name: 'MyClass',
        app_version: '1.0.0',
        academic_year: '2025-2026',
        school_name: 'MyClass School',
        school_address: '123 Education Street, Hong Kong',
        school_phone: '+852 1234 5678',
        school_email: 'info@myclass.edu.hk',
        principal_name: 'Principal Name'
      };

      const { error: appError } = await supabase
        .from('saas_app_setup')
        .upsert(defaultAppSetup);

      if (appError) {
        console.error('Error inserting app setup:', appError);
        showMessage(`Error inserting app setup: ${appError.message}`, 'error');
        return;
      }

      showMessage('Default data inserted successfully!', 'success');
      await checkSetupStatus();

      if (onSetupComplete) {
        onSetupComplete();
      }

    } catch (error) {
      console.error('Error inserting default data:', error);
      showMessage(`Error inserting default data: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const runCompleteSetup = async () => {
    await createTables();
    await insertDefaultData();
  };

  return (
    <div className="app-setup-manager bg-white rounded-lg shadow-md p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
        <span className="mr-2">⚙️</span>
        App Setup Manager
      </h3>
      
      {/* Setup Status */}
      <div className="mb-6 p-4 border border-gray-300 rounded-lg bg-gray-50">
        <h4 className="font-medium text-gray-700 mb-3">Setup Status</h4>
        <div className="space-y-2">
          <div className="flex items-center">
            <span className={`w-3 h-3 rounded-full mr-2 ${setupStatus.tablesCreated ? 'bg-green-500' : 'bg-red-500'}`}></span>
            <span className="text-sm">Database Tables: {setupStatus.tablesCreated ? 'Created' : 'Not Created'}</span>
          </div>
          <div className="flex items-center">
            <span className={`w-3 h-3 rounded-full mr-2 ${setupStatus.appSetup ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
            <span className="text-sm">App Setup: {setupStatus.appSetup ? 'Configured' : 'Not Configured'}</span>
          </div>
          <div className="flex items-center">
            <span className={`w-3 h-3 rounded-full mr-2 ${setupStatus.timetableSetup ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
            <span className="text-sm">Timetable Setup: {setupStatus.timetableSetup ? 'Configured' : 'Not Configured'}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="action-buttons-container">
        <button
          onClick={createTables}
          disabled={loading || setupStatus.tablesCreated}
          className={`action-btn create-tables ${loading ? 'loading' : ''}`}
        >
          <span>🏗️</span>
          {loading ? 'Creating...' : 'Create Tables'}
        </button>
        
        <button
          onClick={insertDefaultData}
          disabled={loading || !setupStatus.tablesCreated}
          className={`action-btn insert-data ${loading ? 'loading' : ''}`}
        >
          <span>📊</span>
          {loading ? 'Inserting...' : 'Insert Default Data'}
        </button>
        
        <button
          onClick={runCompleteSetup}
          disabled={loading}
          className={`action-btn complete-setup ${loading ? 'loading' : ''}`}
        >
          <span>🚀</span>
          {loading ? 'Setting up...' : 'Run Complete Setup'}
        </button>
        
        <button
          onClick={checkSetupStatus}
          disabled={loading}
          className={`action-btn refresh-status ${loading ? 'loading' : ''}`}
        >
          <span>🔄</span>
          Refresh Status
        </button>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`mt-4 p-3 rounded-lg ${
          messageType === 'success' ? 'bg-green-100 text-green-800 border border-green-200' :
          messageType === 'error' ? 'bg-red-100 text-red-800 border border-red-200' :
          'bg-blue-100 text-blue-800 border border-blue-200'
        }`}>
          <div className="flex items-center">
            <span className="mr-2">
              {messageType === 'success' ? '✅' : messageType === 'error' ? '❌' : 'ℹ️'}
            </span>
            {message}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-700 mb-2">Setup Instructions:</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• <strong>Create Tables:</strong> Creates the necessary database tables for the app</li>
          <li>• <strong>Insert Default Data:</strong> Adds default configuration data to the tables</li>
          <li>• <strong>Run Complete Setup:</strong> Performs both table creation and data insertion</li>
          <li>• <strong>Refresh Status:</strong> Checks the current setup status</li>
        </ul>
        <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
          <p className="text-sm text-blue-700">
            <strong>Note:</strong> Run the complete setup first to initialize all necessary tables and data. 
            After setup is complete, you can configure timetable settings and upload teacher files.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AppSetupManager;
